import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';

import AtacamaExperiencesSection from '../../components/AtacamaExperiencesSection/AtacamaExperiencesSection';
import ImageTextPaginated from '../../components/shared/ImageTextPaginated/ImageTextPaginated';
import {BlogSection} from '../../components/BrandHomePage/BlogSection/BlogSection';
import HorizontalCarousel from '../../components/HorizontalCarousel/HorizontalCarousel';
import PropertyPanels from '../../components/HorizontalCarousel/panels/PropertyPanels';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import ResortMap from '../../components/ResortMap/ResortMap';
import Hero from '../../components/Hero/Hero';
import Footer from '../../components/Footer/Footer';

import { OffersGrid } from '../../components/shared/OffersGrid/OffersGrid';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { images } from '../../components/shared/assets';

import '../../styles/property-pages.css';
import './PatagoniaPage.css';
import BrandHomePage from '../BrandHomePage/BrandHomePage';

const PatagoniaPageHardcoded: React.FC = () => {
  const { setHoveredDestination } = useOverlayManager();
  const { t } = useTranslation();

  // Initialize all interactive features
  useEffect(() => {
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);
  }, []);


  return (
    <div className="property-page property-page--patagonia">

      <title>{t('meta:patagonia.home.title')}</title>
      <meta name="description" content={t('meta:patagonia.home.description')} />
      <meta property="og:title" content={t('meta:patagonia.home.title')} />
      <meta property="og:description" content={t('meta:patagonia.home.description')} />



      {/* Navigation - Transparent variant for hero section */}
      <UnifiedNavigation variant="transparent" destination="patagonia" />

      {/* Hero Section */}
      <Hero
        backgroundVideo='https://videos.ctfassets.net/hz1mviiqoqof/3QhpVE92WXYwNJzMC1V0d8/d14d7d7b50e228a507fbdaab8be3a874/hero-patagonia.mp4'
        heading={t('properties/patagonia:hero.title')}
      />

      {/* Content Sections with Scroll Reveals */}
      <div className="content-wrapper">

        <HorizontalCarousel>
          <PropertyPanels property="patagonia" />
        </HorizontalCarousel>

        {/* Full Viewport Video Section */}
        <img src="/assets/images/patagonia/patagonia-01.png" className="video-background" />

        {/* Experience Your Own Path Private Excursions Section */}
        <div data-scroll-section="experiences" className="scroll-reveal-section">
          <AtacamaExperiencesSection
            property="patagonia"
            title={t('properties/patagonia:experience.title')}
            description={t('properties/patagonia:experience.description')}
            restaurantTitle="Fresh fish from the South Pacific fjords. Seasonal produce, local flavours, based on our guests preferences."
            restaurantDescription="Our restaurant, for the exclusive use of Awasi guests, offers a daily menu using the finest local ingredients, adapted to your requirements. We are proud to deliver levels of culinary excellence that define us as members of Relais & Châteaux."
            experiences={[
              {
                image: images.experiences.patagonia.experience1,
                title: t('properties/patagonia:excursions.0.title'),
                category: t('properties/patagonia:excursions.0.location'),
                description: t('properties/patagonia:excursions.0.description')
              },
              {
                image: images.experiences.patagonia.experience2,
                title: t('properties/patagonia:excursions.1.title'),
                category: t('properties/patagonia:excursions.1.location'),
                description: t('properties/patagonia:excursions.1.description')
              },
              {
                image: images.experiences.patagonia.experience3,
                title: t('properties/patagonia:excursions.2.title'),
                category: t('properties/patagonia:excursions.2.location'),
                description: t('properties/patagonia:excursions.2.description')
              },
              {
                image: images.experiences.patagonia.experience4,
                title: t('properties/patagonia:excursions.3.title'),
                category: t('properties/patagonia:excursions.3.location'),
                description: t('properties/patagonia:excursions.3.description')
              }
            ]}
          />
        </div>

        {/* Dining Section */}
        <div data-scroll-section="dining" className="scroll-reveal-section">
          <ImageTextPaginated
            backgroundColor="#C0C6C8"
            foregroundColor="#49413D"
            heading={t('properties/patagonia:restaurant.heading')}
            slides={[
              {
                heading: t('properties/patagonia:restaurant.card1.title'),
                description: t('properties/patagonia:restaurant.card1.description'),
                linkUrl: "/patagonia/dining",
                linkLabel: t('properties/patagonia:restaurant.card1.linkLabel'),
                imageUrl: "/assets/images/patagonia/dining-01.png"
              },
              {
                heading: t('properties/patagonia:restaurant.card2.title'),
                description: t('properties/patagonia:restaurant.card2.description'),
                linkUrl: "/patagonia/dining",
                linkLabel: t('properties/patagonia:restaurant.card2.linkLabel'),
                imageUrl: "/assets/images/patagonia/dining-01.png"
              },
              {
                heading: t('properties/patagonia:restaurant.card3.title'),
                description: t('properties/patagonia:restaurant.card3.description'),
                linkUrl: "/patagonia/dining",
                linkLabel: t('properties/patagonia:restaurant.card3.linkLabel'),
                imageUrl: "/assets/images/patagonia/dining-01.png"
              },
            ]}
          />

        </div>

        {/* Offers Section */}
        <div data-scroll-section="offers" className="scroll-reveal-section">
          <OffersGrid
            heading={t('properties/patagonia:offers.heading')}
            description={t('properties/patagonia:offers.copy')}
            ctaLabel={t('properties/patagonia:offers.cta')}
            ctaLink="/packages"
            offers={
              [
                {
                  id: 1,
                  image: '/assets/images/offers/offer-01.jpg',
                  tag: t('pages/home:offers.tag'),
                  title: t('pages/home:offers.items.combine.title'),
                  moreLink: "/packages/combine-three",
                  moreLabel: t('properties/santa-catarina:journey.cards.combine.viewMore')
                },
                {
                  id: 2,
                  image: 'https://images.ctfassets.net/hz1mviiqoqof/15Cp3o6XVDv7icoa9wFixB/bc2c3f2c8e8fade33be67101c8b93cd0/rainbow-falls.jpg',
                  tag: t('pages/home:offers.tag'),
                  title: t('pages/home:offers.items.stayLonger.title'),
                  moreLink: "/packages/stay-longer",
                  moreLabel: t('properties/santa-catarina:journey.cards.combine.viewMore')
                },
                // {
                //   id: 3,
                //   image: '/assets/Awasi Atacama-17.jpg',
                //   tag: t('pages/home:offers.tag'),
                //   title: t('pages/home:offers.items.wellness.title'),
                //   moreLink: "/packages/re-energize",
                //   moreLabel: t('properties/santa-catarina:journey.cards.combine.viewMore')
                // }
              ]
            }
          />
        </div>


        {/* Map Section */}
        <div data-scroll-section="map" className="scroll-reveal-section">
          <ResortMap
            backgroundImage="/assets/images/patagonia/patagonia-01.png"
            foregroundColor="#49413D"
            backgroundColor="#C0C6C8"
            mapImageUrl="/assets/images/patagonia/map.png"
            heading={t('properties/patagonia:map.title')}
            fromList={
              [
                {
                  "heading": t('properties/patagonia:map.fromChile.title'),
                  "description": t('properties/patagonia:map.fromChile.description')
                },
                {
                  "heading": t('properties/patagonia:map.nearbyLandBorders.title'),
                  "description": t('properties/patagonia:map.nearbyLandBorders.description')
                }
              ]
            }

          />
        </div>

        {/* Blog Section */}
        <div data-scroll-section="blog" className="scroll-reveal-section">
          <BlogSection />
        </div>

        {/* Instagram Section */}
        <InstagramSection />

        {/* Footer */}
        <Footer variant="patagonia" />
      </div>
    </div>
  );
};

export default PatagoniaPageHardcoded;